import React, {MutableRefObject, RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {getRectOfNodes, useReactFlow} from 'reactflow';
import type {Node, ReactFlowInstance} from 'reactflow';

import {
  ConditionGroup,
  getMethodIdsImplementingConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import BaseControlButton from 'ui-library/reactflow/controls/BaseControlButton';
import {isFlowchartElementHiddenBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import {centerNodeInVisibleCanvas} from 'ui-library/reactflow/viewport/centerNodeInVisibleCanvas';

import {getProcedureRunDesignFlowchartAddReplicateNodeId} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {
  getProcedureRunDesignFlowchartColumnNodeId,
  PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
} from './ProcedureRunDesignFlowchartColumnNode';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

// Flex sibling panels leave the wrapper equal to the visible canvas.
const NULL_PANEL_REF: RefObject<HTMLDivElement> = {current: null};

/** When a pending method column scroll should center horizontally. */
export type MethodColumnScrollMode = 'always' | 'whenObscured';

export interface UseProcedureRunDesignFlowchartAutoScrollArgs {
  conditions: Array<ConditionGroup>;
  /** Flowchart columns; a condition with no coverage map spans all of them. */
  methods: ReadonlyArray<{id: string}>;
  activeMethodId: string | null;
  isEditable: boolean;
  /** Live nodes merged by the layout hooks. */
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  /** Marks in-canvas adds until they are detected. */
  lastAddSourceRef?: MutableRefObject<'inCanvas' | null>;
  /** One-shot method column scroll. Pane deselects do not set it. */
  pendingMethodIdToScroll?: string | null;
  /** {@link MethodColumnScrollMode} for {@link pendingMethodIdToScroll}. Defaults to {@code always}. */
  pendingMethodScrollMode?: MethodColumnScrollMode;
  /** Clears {@link pendingMethodIdToScroll} after the hook handles the request. */
  onMethodScrollConsumed?: () => void;
  /**
   * Current canvas selection. When it differs from {@link pendingMethodIdToScroll}, the pending
   * dropdown request is discarded so a deferred layout pass cannot pan to a stale method.
   */
  selectedMethodId?: string | null;
  /**
   * The right ("Design") sidepanel, which is absolutely positioned and overlays the canvas rather
   * than shrinking the wrapper. Passed to centering so the selected method column lands centered in
   * the canvas area still visible beside the panel, not behind it.
   */
  rightSidepanelRef?: RefObject<HTMLDivElement>;
}

export interface UseProcedureRunDesignFlowchartAutoScrollResult {
  additionalAutoScrollControls: JSX.Element;
}

/**
 * Counts condition-replicate cells (replicates times covered methods) to detect additions. Counting
 * cells rather than group replicates keeps the count growing when an added plan row merges per-method
 * groups into fewer shared ones.
 */
function getReplicateCellCount(
  conditions: Array<ConditionGroup>,
  methods: ReadonlyArray<{id: string}>
): number {
  return conditions.reduce(
    (total, condition) =>
      total +
      condition.numReplicates * (getMethodIdsImplementingConditionGroup(condition)?.size ?? methods.length),
    0
  );
}

function isRowNode(node: ProcedureRunDesignFlowchartLayoutNode): boolean {
  // Column overlays span the full height, so exclude them from row calculations.
  return node.type !== PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE;
}

function getRowNodeIds(nodes: Array<ProcedureRunDesignFlowchartLayoutNode>): Array<string> {
  return nodes.filter((node) => isRowNode(node)).map((node) => node.id);
}

function isNodeLaidOut(node: ProcedureRunDesignFlowchartLayoutNode): boolean {
  return (node.width ?? 0) > 0 && (node.height ?? 0) > 0 && !isFlowchartElementHiddenBeforeLayout(node);
}

type AddSource = 'inCanvas' | 'nonCanvas';

interface UsePendingRowAdditionArgs {
  conditions: Array<ConditionGroup>;
  methods: ReadonlyArray<{id: string}>;
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  lastAddSourceRef?: MutableRefObject<'inCanvas' | null>;
}

interface PendingRowAddition {
  cellCount: number;
  preAddRowNodeIdsRef: MutableRefObject<Set<string>>;
  isScrollPendingRef: MutableRefObject<boolean>;
  pendingAddSourceRef: MutableRefObject<AddSource>;
}

function usePendingRowAddition({
  conditions,
  methods,
  nodes,
  lastAddSourceRef,
}: UsePendingRowAdditionArgs): PendingRowAddition {
  const cellCount = getReplicateCellCount(conditions, methods);
  const previousCellCountRef = useRef(cellCount);
  const preAddRowNodeIdsRef = useRef<Set<string>>(new Set(getRowNodeIds(nodes)));
  const isScrollPendingRef = useRef(false);
  const pendingAddSourceRef = useRef<AddSource>('nonCanvas');

  useEffect(() => {
    if (cellCount > previousCellCountRef.current) {
      preAddRowNodeIdsRef.current = new Set(getRowNodeIds(nodes));
      pendingAddSourceRef.current = lastAddSourceRef?.current === 'inCanvas' ? 'inCanvas' : 'nonCanvas';
      if (lastAddSourceRef != null) {
        lastAddSourceRef.current = null;
      }
      isScrollPendingRef.current = true;
    }
    previousCellCountRef.current = cellCount;
  }, [cellCount, nodes, lastAddSourceRef]);

  return {cellCount, preAddRowNodeIdsRef, isScrollPendingRef, pendingAddSourceRef};
}

interface NodeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AutoScrollAxes {
  horizontal: boolean;
  vertical: boolean;
}

interface GetAxesToCenterArgs {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  horizontalBounds: NodeBounds;
  verticalBounds: NodeBounds;
  allowHorizontal: boolean;
  /**
   * The absolutely positioned right ("Design") panel, which overlays the canvas rather than
   * shrinking the wrapper. Its width is subtracted from the right edge so a node hidden behind the
   * panel counts as off-canvas and gets panned into view. Defaults to no panel.
   */
  rightSidepanelRef?: RefObject<HTMLDivElement>;
}

/** Returns allowed axes that require panning, or null when the canvas cannot be measured. */
function getAxesToCenter({
  reactFlowInstance,
  reactFlowWrapper,
  horizontalBounds,
  verticalBounds,
  allowHorizontal,
  rightSidepanelRef,
}: GetAxesToCenterArgs): AutoScrollAxes | null {
  const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
  if (wrapperRect == null || wrapperRect.width <= 0 || wrapperRect.height <= 0) {
    return null;
  }

  const rightPanelWidth = rightSidepanelRef?.current?.getBoundingClientRect().width ?? 0;
  const visibleRight = wrapperRect.right - rightPanelWidth;

  const {x, y, zoom} = reactFlowInstance.getViewport();
  const nodeLeft = wrapperRect.left + horizontalBounds.x * zoom + x;
  const nodeRight = nodeLeft + horizontalBounds.width * zoom;
  const nodeTop = wrapperRect.top + verticalBounds.y * zoom + y;
  const nodeBottom = nodeTop + verticalBounds.height * zoom;

  return {
    horizontal: allowHorizontal && (nodeLeft < wrapperRect.left || nodeRight > visibleRight),
    vertical: nodeTop < wrapperRect.top || nodeBottom > wrapperRect.bottom,
  };
}

export interface IsMethodColumnObscuredArgs {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  rightSidepanelRef: RefObject<HTMLDivElement>;
  columnBounds: NodeBounds;
}

/**
 * True when any part of the column is outside the canvas beside the right panel. Returns null if the
 * canvas is not measurable yet.
 */
export function isMethodColumnObscured({
  reactFlowInstance,
  reactFlowWrapper,
  rightSidepanelRef,
  columnBounds,
}: IsMethodColumnObscuredArgs): boolean | null {
  const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
  if (wrapperRect == null || wrapperRect.width <= 0 || wrapperRect.height <= 0) {
    return null;
  }

  const rightPanelWidth = rightSidepanelRef.current?.getBoundingClientRect().width ?? 0;
  const visibleRight = wrapperRect.right - rightPanelWidth;

  const {x, zoom} = reactFlowInstance.getViewport();
  const nodeLeft = wrapperRect.left + columnBounds.x * zoom + x;
  const nodeRight = nodeLeft + columnBounds.width * zoom;

  return nodeLeft < wrapperRect.left || nodeRight > visibleRight;
}

interface AutoScrollTarget {
  horizontalAnchor: NodeBounds;
  verticalTarget: NodeBounds;
}

interface GetAutoScrollTargetArgs {
  reactFlowNodes: Array<Node>;
  newRowNodeIds: ReadonlySet<string>;
  addReplicateNodeId: string | null;
}

/** Selects visibility anchors and the bounds to center after an add. */
function getAutoScrollTarget({
  reactFlowNodes,
  newRowNodeIds,
  addReplicateNodeId,
}: GetAutoScrollTargetArgs): AutoScrollTarget | null {
  let addReplicateNode: Node | null = null;
  let horizontalAnchor: NodeBounds | null = null;
  let bottomRowY = Number.NEGATIVE_INFINITY;
  let bottomRowNodes: Array<Node> = [];

  for (const node of reactFlowNodes) {
    if (node.id === addReplicateNodeId) {
      addReplicateNode = node;
    }
    if (!newRowNodeIds.has(node.id)) {
      continue;
    }

    const bounds = getRectOfNodes([node]);
    if (horizontalAnchor == null || bounds.x < horizontalAnchor.x) {
      horizontalAnchor = bounds;
    }
    if (bounds.y > bottomRowY) {
      bottomRowY = bounds.y;
      bottomRowNodes = [node];
    } else if (bounds.y === bottomRowY) {
      bottomRowNodes.push(node);
    }
  }

  if (horizontalAnchor == null || bottomRowNodes.length === 0) {
    return null;
  }

  return {
    horizontalAnchor,
    verticalTarget: getRectOfNodes(
      addReplicateNode == null ? bottomRowNodes : [...bottomRowNodes, addReplicateNode]
    ),
  };
}

/** Canvas handles and enablement state shared by every auto-scroll behavior. */
interface AutoScrollContext {
  isAutoScrollEnabled: boolean;
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  /**
   * The absolutely positioned right ("Design") panel. Both scroll behaviors exclude it from the
   * visible canvas so nodes are revealed and centered beside the panel, not behind it.
   */
  rightSidepanelRef: RefObject<HTMLDivElement>;
}

interface UsePendingRowScrollArgs extends AutoScrollContext {
  pendingRowAddition: PendingRowAddition;
  activeMethodId: string | null;
  isEditable: boolean;
}

/** Pans to reveal newly added replicate rows once they have been laid out. */
function usePendingRowScroll({
  pendingRowAddition,
  isAutoScrollEnabled,
  nodes,
  activeMethodId,
  isEditable,
  reactFlowInstance,
  reactFlowWrapper,
  rightSidepanelRef,
}: UsePendingRowScrollArgs): void {
  const {cellCount, preAddRowNodeIdsRef, isScrollPendingRef, pendingAddSourceRef} = pendingRowAddition;

  useEffect(() => {
    if (!isAutoScrollEnabled) {
      isScrollPendingRef.current = false;
      return;
    }

    if (!isScrollPendingRef.current) {
      return;
    }

    const newRowNodes = nodes.filter((node) => isRowNode(node) && !preAddRowNodeIdsRef.current.has(node.id));
    if (newRowNodes.length === 0 || !newRowNodes.every((node) => isNodeLaidOut(node))) {
      return;
    }

    const addReplicateNodeId =
      isEditable && activeMethodId != null
        ? getProcedureRunDesignFlowchartAddReplicateNodeId(activeMethodId)
        : null;

    // Store nodes include positionAbsolute.
    const reactFlowNodes = reactFlowInstance.getNodes();
    const newRowNodeIds = new Set(
      newRowNodes.filter((node) => node.id !== addReplicateNodeId).map((node) => node.id)
    );
    const target = getAutoScrollTarget({reactFlowNodes, newRowNodeIds, addReplicateNodeId});
    if (target == null) {
      return;
    }

    const axes = getAxesToCenter({
      reactFlowInstance,
      reactFlowWrapper,
      horizontalBounds: target.horizontalAnchor,
      verticalBounds: target.verticalTarget,
      allowHorizontal: pendingAddSourceRef.current === 'nonCanvas',
      rightSidepanelRef,
    });
    if (axes == null) {
      return;
    }
    if (!axes.horizontal && !axes.vertical) {
      isScrollPendingRef.current = false;
      return;
    }

    // Clear before panning to avoid repeat pans.
    isScrollPendingRef.current = false;
    centerNodeInVisibleCanvas({
      reactFlowInstance,
      reactFlowWrapper,
      // The left panel is a flex sibling that already shrinks the wrapper; only the absolutely
      // positioned right panel needs excluding from the centering target.
      leftSidepanelRef: NULL_PANEL_REF,
      rightSidepanelRef,
      nodePosition: {x: target.horizontalAnchor.x, y: target.verticalTarget.y},
      nodeWidth: target.horizontalAnchor.width,
      nodeHeight: target.verticalTarget.height,
      axes,
    });
  }, [
    cellCount,
    nodes,
    activeMethodId,
    isEditable,
    reactFlowInstance,
    reactFlowWrapper,
    rightSidepanelRef,
    isAutoScrollEnabled,
    preAddRowNodeIdsRef,
    isScrollPendingRef,
    pendingAddSourceRef,
  ]);
}

interface UsePendingMethodScrollArgs extends AutoScrollContext {
  pendingMethodIdToScroll: string | null;
  pendingMethodScrollMode: MethodColumnScrollMode;
  selectedMethodId?: string | null;
  onMethodScrollConsumed?: () => void;
}

function centerMethodColumnInVisibleCanvas({
  reactFlowInstance,
  reactFlowWrapper,
  rightSidepanelRef,
  bounds,
}: {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  rightSidepanelRef: RefObject<HTMLDivElement>;
  bounds: NodeBounds;
}): void {
  centerNodeInVisibleCanvas({
    reactFlowInstance,
    reactFlowWrapper,
    // Only the absolutely positioned right panel overlays the canvas.
    leftSidepanelRef: NULL_PANEL_REF,
    rightSidepanelRef,
    nodePosition: {x: bounds.x, y: bounds.y},
    nodeWidth: bounds.width,
    nodeHeight: bounds.height,
    axes: {horizontal: true, vertical: false},
  });
}

/** Centers the requested method column horizontally. Skips vertical centering. */
function usePendingMethodScroll({
  pendingMethodIdToScroll,
  pendingMethodScrollMode,
  selectedMethodId,
  onMethodScrollConsumed,
  isAutoScrollEnabled,
  nodes,
  reactFlowInstance,
  reactFlowWrapper,
  rightSidepanelRef,
}: UsePendingMethodScrollArgs): void {
  useEffect(() => {
    if (pendingMethodIdToScroll == null) {
      return;
    }

    // Canvas clicks and panel close already clear this request. Keep this check as a safety net in
    // case the controlled selection changes while we are waiting for layout.
    if (selectedMethodId !== undefined && pendingMethodIdToScroll !== selectedMethodId) {
      onMethodScrollConsumed?.();
      return;
    }

    if (!isAutoScrollEnabled) {
      onMethodScrollConsumed?.();
      return;
    }

    const columnNodeId = getProcedureRunDesignFlowchartColumnNodeId(pendingMethodIdToScroll);
    const columnLayoutNode = nodes.find((node) => node.id === columnNodeId);
    if (columnLayoutNode == null) {
      // Dropdown options and columns come from the same methods list, so this should not normally
      // happen. Treat it as a stale request instead of leaving it pending indefinitely.
      onMethodScrollConsumed?.();
      return;
    }
    if (!isNodeLaidOut(columnLayoutNode)) {
      // Wait for layout measurement before consuming the request.
      return;
    }

    // Store nodes include positionAbsolute.
    const columnNode = reactFlowInstance.getNodes().find((node) => node.id === columnNodeId);
    if (columnNode == null) {
      onMethodScrollConsumed?.();
      return;
    }

    const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
    if (wrapperRect == null || wrapperRect.width <= 0 || wrapperRect.height <= 0) {
      // Canvas not measurable yet. Retry on the next nodes update.
      return;
    }

    const bounds = getRectOfNodes([columnNode]);

    if (pendingMethodScrollMode === 'whenObscured') {
      const isObscured = isMethodColumnObscured({
        reactFlowInstance,
        reactFlowWrapper,
        rightSidepanelRef,
        columnBounds: bounds,
      });
      if (isObscured == null) {
        return;
      }
      onMethodScrollConsumed?.();
      if (!isObscured) {
        return;
      }
    } else {
      onMethodScrollConsumed?.();
    }

    centerMethodColumnInVisibleCanvas({reactFlowInstance, reactFlowWrapper, rightSidepanelRef, bounds});
  }, [
    pendingMethodIdToScroll,
    pendingMethodScrollMode,
    selectedMethodId,
    onMethodScrollConsumed,
    isAutoScrollEnabled,
    nodes,
    reactFlowInstance,
    reactFlowWrapper,
    rightSidepanelRef,
  ]);
}

/**
 * Auto-scrolls newly added replicate rows and the dropdown-selected method column into view. A
 * control toggles the behavior off.
 */
export function useProcedureRunDesignFlowchartAutoScroll({
  conditions,
  methods,
  activeMethodId,
  isEditable,
  nodes,
  reactFlowWrapper,
  lastAddSourceRef,
  pendingMethodIdToScroll = null,
  pendingMethodScrollMode = 'always',
  onMethodScrollConsumed,
  selectedMethodId,
  rightSidepanelRef = NULL_PANEL_REF,
}: UseProcedureRunDesignFlowchartAutoScrollArgs): UseProcedureRunDesignFlowchartAutoScrollResult {
  const reactFlowInstance = useReactFlow();
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const toggleAutoScroll = useCallback(() => {
    setIsAutoScrollEnabled((isEnabled) => !isEnabled);
  }, []);
  const pendingRowAddition = usePendingRowAddition({
    conditions,
    methods,
    nodes,
    lastAddSourceRef,
  });

  const autoScrollContext: AutoScrollContext = {
    isAutoScrollEnabled,
    nodes,
    reactFlowInstance,
    reactFlowWrapper,
    rightSidepanelRef,
  };

  usePendingRowScroll({
    ...autoScrollContext,
    pendingRowAddition,
    activeMethodId,
    isEditable,
  });

  usePendingMethodScroll({
    ...autoScrollContext,
    pendingMethodIdToScroll,
    pendingMethodScrollMode,
    selectedMethodId,
    onMethodScrollConsumed,
  });

  const additionalAutoScrollControls = (
    <BaseControlButton
      icon={isAutoScrollEnabled ? 'location-crosshairs-slash' : 'location-crosshairs'}
      tooltip={isAutoScrollEnabled ? 'Turn off auto scroll' : 'Turn on auto scroll'}
      onClick={toggleAutoScroll}
      dataTestAttrs={{component: 'AutoScrollButton', element: 'root'}}
    />
  );

  return {additionalAutoScrollControls};
}
